import { useMemo } from 'react'
import Card from '../../../components/common/Card/Card'

const TimesheetSummary = ({ entries = [], employeeId }) => {
  // Helper: Get current week (Monday-Sunday)
  const getCurrentWeek = () => {
    const today = new Date()
    const dayOfWeek = today.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const monday = new Date(today)

    // Calculate days to subtract to get to Monday
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
    monday.setDate(today.getDate() - daysToMonday)

    // Calculate Sunday
    const sunday = new Date(monday)
    sunday.setDate(monday.getDate() + 6)

    const weekDates = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday)
      date.setDate(monday.getDate() + i)
      weekDates.push(date.toISOString().split('T')[0])
    }

    return {
      startDate: monday.toISOString().split('T')[0],
      endDate: sunday.toISOString().split('T')[0],
      weekDates,
      monday,
      sunday
    }
  }

  // Calculate weekly statistics
  const weeklyStats = useMemo(() => {
    const week = getCurrentWeek()
    const today = new Date().toISOString().split('T')[0]

    // Filter entries to current week (pending and approved only)
    const weekEntries = entries.filter(entry => {
      if (!entry.date) return false
      if (entry.status === 'cancelled' || entry.status === 'rejected') return false
      return entry.date >= week.startDate && entry.date <= week.endDate
    })

    // Calculate total hours
    const totalHours = weekEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0)

    // Group by project
    const projectMap = {}
    weekEntries.forEach(entry => {
      const projectName = entry.projectName || 'Unknown Project'
      if (!projectMap[projectName]) {
        projectMap[projectName] = 0
      }
      projectMap[projectName] += entry.hours || 0
    })

    const projects = Object.entries(projectMap)
      .map(([name, hours]) => ({ name, hours }))
      .sort((a, b) => b.hours - a.hours) // Sort by hours descending

    // Group by day
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    const dailyBreakdown = week.weekDates.map((date, index) => {
      const dayEntries = weekEntries.filter(entry => entry.date === date)
      const dayHours = dayEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0)
      return {
        date,
        dayName: dayNames[index],
        hours: dayHours,
        isToday: date === today
      }
    })

    // Format week range
    const formatWeekRange = () => {
      const options = { month: 'short', day: 'numeric' }
      const startStr = week.monday.toLocaleDateString('en-US', options)
      const endStr = week.sunday.toLocaleDateString('en-US', options)
      const year = week.monday.getFullYear()
      return `${startStr} - ${endStr}, ${year}`
    }

    return {
      totalHours,
      projects,
      dailyBreakdown,
      weekRange: formatWeekRange(),
      hasEntries: weekEntries.length > 0
    }
  }, [entries])

  // Empty state
  if (!weeklyStats.hasEntries) {
    return (
      <Card style={{ marginBottom: '2rem' }}>
        <h3 style={{
          margin: '0 0 1rem 0',
          fontSize: 'var(--psyber-font-size-xl)',
          color: 'var(--psyber-text)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <i className="fas fa-chart-bar"></i>
          Weekly Summary ({weeklyStats.weekRange})
        </h3>
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: 'var(--psyber-text-secondary)'
        }}>
          <i className="fas fa-calendar-week" style={{
            fontSize: '3rem',
            marginBottom: '1rem',
            display: 'block',
            color: 'var(--psyber-text-muted)'
          }}></i>
          <p>No timesheet entries for this week</p>
        </div>
      </Card>
    )
  }

  return (
    <Card style={{ marginBottom: '2rem' }}>
      <h3 style={{
        margin: '0 0 1.5rem 0',
        fontSize: 'var(--psyber-font-size-xl)',
        color: 'var(--psyber-text)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <i className="fas fa-chart-bar"></i>
        Weekly Summary ({weeklyStats.weekRange})
      </h3>

      <div className="psyber-grid psyber-grid-3" style={{ gap: '1.5rem' }}>
        {/* Total Hours Card */}
        <Card style={{ textAlign: 'center' }}>
          <i className="fas fa-clock" style={{
            fontSize: '2.5rem',
            color: 'var(--psyber-primary)',
            marginBottom: '0.75rem'
          }}></i>
          <h4 style={{
            fontSize: 'var(--psyber-font-size-2xl)',
            margin: '0.5rem 0',
            color: 'var(--psyber-text)'
          }}>
            {weeklyStats.totalHours.toFixed(1)}h
          </h4>
          <p style={{
            margin: 0,
            color: 'var(--psyber-text-secondary)',
            fontSize: 'var(--psyber-font-size-sm)'
          }}>
            Total Hours
          </p>
        </Card>

        {/* Projects Breakdown */}
        <Card>
          <h4 style={{
            margin: '0 0 1rem 0',
            fontSize: 'var(--psyber-font-size-lg)',
            color: 'var(--psyber-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="fas fa-project-diagram"></i>
            By Project
          </h4>
          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
            {weeklyStats.projects.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--psyber-text-secondary)', fontSize: 'var(--psyber-font-size-sm)' }}>
                No projects
              </p>
            ) : (
              weeklyStats.projects.map((project, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem 0',
                    borderBottom: index < weeklyStats.projects.length - 1 ? '1px solid var(--psyber-card-border)' : 'none'
                  }}
                >
                  <span style={{
                    color: 'var(--psyber-text)',
                    fontSize: 'var(--psyber-font-size-sm)',
                    fontWeight: 500
                  }}>
                    {project.name}
                  </span>
                  <span style={{
                    color: 'var(--psyber-primary)',
                    fontSize: 'var(--psyber-font-size-sm)',
                    fontWeight: 600
                  }}>
                    {project.hours.toFixed(1)}h
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Daily Breakdown */}
        <Card>
          <h4 style={{
            margin: '0 0 1rem 0',
            fontSize: 'var(--psyber-font-size-lg)',
            color: 'var(--psyber-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="fas fa-calendar-week"></i>
            By Day
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '0.5rem'
          }}>
            {weeklyStats.dailyBreakdown.map((day, index) => (
              <div
                key={index}
                style={{
                  textAlign: 'center',
                  padding: '0.5rem',
                  background: day.isToday ? 'var(--psyber-primary-opacity-10)' : 'var(--psyber-card-hover)',
                  borderRadius: 'var(--psyber-radius-sm)',
                  border: day.isToday ? '2px solid var(--psyber-primary)' : 'none'
                }}
              >
                <div style={{
                  fontSize: 'var(--psyber-font-size-xs)',
                  fontWeight: 600,
                  color: day.isToday ? 'var(--psyber-primary)' : 'var(--psyber-text-secondary)',
                  marginBottom: '0.25rem'
                }}>
                  {day.dayName}
                </div>
                <div style={{
                  fontSize: 'var(--psyber-font-size-sm)',
                  fontWeight: 600,
                  color: day.isToday ? 'var(--psyber-primary)' : 'var(--psyber-text)'
                }}>
                  {day.hours.toFixed(1)}h
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Card>
  )
}

export default TimesheetSummary
